import BugsnagPerformance, { type BrowserConfiguration } from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import { VueRouterRoutingProvider } from '@bugsnag/vue-router-performance';
import type { Router } from 'vue-router';
import sanitizeUrl, { type SanitizeUrlOptions } from '../utils/sanitize-url';

interface ConfigureBugsnagPerformanceOptions {
	apiKey: string;
	appVersion: string;
	enabledReleaseStages: string[];
	getProjectImageBaseUrl: () => string | null;
	getUrlOptions?: () => SanitizeUrlOptions;
	includeImageSpans?: boolean;
	releaseStage: string;
	router: Router;
	serviceName: string;
}

type NetworkRequestInfo = Parameters<NonNullable<BrowserConfiguration['networkRequestCallback']>>[0];
type BugsnagSpan = ReturnType<typeof BugsnagPerformance.startSpan>;

let performanceMonitoringStarted = false;

/**
 * Keeps network performance grouping useful without sending credentials, query
 * parameters or fragments, which can contain Creator access codes, user tokens,
 * or customer data. Project image requests collapse to their configured base
 * endpoint because every suffix contains a project id, read token and version.
 *
 * @param requestInfo - The network request details captured by the browser SDK.
 * @param options - URL prefixes containing application-specific capabilities.
 * @returns A copy safe for telemetry, reduced to the request origin and path.
 */
function sanitizeNetworkRequest(
	requestInfo: NetworkRequestInfo,
	options: SanitizeUrlOptions,
): NetworkRequestInfo {
	const sanitizedUrl = (
		requestInfo.url
		&& sanitizeUrl(
			requestInfo.url,
			options,
		)
	);

	return {
		...requestInfo,
		propagateTraceContext: false,
		url: sanitizedUrl,
	};
}

/**
 * Starts BugSnag real-user performance monitoring against Creator's existing
 * error project and Vue Router. The SDK's default automatic instrumentation
 * already covers full page loads, route changes, resource loads, and fetch/XHR
 * requests, so only Creator's deviations from the defaults are set here.
 *
 * @param options - Existing BugSnag release metadata plus Creator's router.
 */
export function configureBugsnagPerformance(
	options: ConfigureBugsnagPerformanceOptions,
): void {
	BugsnagPerformance.start({
		apiKey: options.apiKey,
		appVersion: options.appVersion,
		bugsnag: Bugsnag,
		enabledReleaseStages: options.enabledReleaseStages,
		networkRequestCallback: (requestInfo) => {
			if (options.includeImageSpans === false && requestInfo.type === 'img') {
				return null;
			}

			return sanitizeNetworkRequest(
				requestInfo,
				{
					collapsePathPrefixes: [
						options.getProjectImageBaseUrl(),
						...(options.getUrlOptions?.().collapsePathPrefixes ?? []),
					],
				},
			);
		},
		releaseStage: options.releaseStage,
		routingProvider: new VueRouterRoutingProvider(
			options.router,
			options.router.options.history.base,
		),
		sendPageAttributes: {
			referrer: false,
			url: false,
		},
		serviceName: options.serviceName,
	});
	performanceMonitoringStarted = true;
}

/**
 * Starts a custom span only when the performance client has been configured.
 * Storybook and test presets can call application operations without booting
 * monitoring, so they receive no span instead of an SDK lifecycle error.
 *
 * @param name - Stable, low-cardinality operation name.
 * @returns A BugSnag span, or undefined outside monitored application boots.
 */
export function startBugsnagSpan(name: string): BugsnagSpan | undefined {
	if (!performanceMonitoringStarted) {
		return undefined;
	}

	return BugsnagPerformance.startSpan(name);
}
